/**
 * REW-929 Paper Journal admin service.
 *
 * Thin axios wrapper — unwraps AxiosResponse and rethrows errors so callers
 * can branch on `error.response?.status`.
 */

import { api } from '@/app/services/apiClient';
import { API_ENDPOINTS } from '@/app/config/api';
import type {
  PaperJournalListUploadsParams,
  PaperJournalPaginatedRewardRows,
  PaperJournalRewardRow,
  PaperJournalUploadHistoryResponse,
  PaperJournalUploadResponse,
} from '@/app/types/paperJournal';

export interface IPaperJournalService {
  getPaperJournalUploads(
    params: PaperJournalListUploadsParams,
  ): Promise<PaperJournalUploadHistoryResponse>;
  uploadPaperJournal(file: File): Promise<PaperJournalUploadResponse>;
  getPaperJournalUploadRows(fileSha256: string): Promise<PaperJournalRewardRow[]>;
  getPaperJournalUserRows(
    refUserId: number,
    limit: number,
    offset: number,
  ): Promise<PaperJournalPaginatedRewardRows>;
  getPaperJournalEpidRows(
    eligibleParticipantId: string | number,
    limit: number,
    offset: number,
  ): Promise<PaperJournalPaginatedRewardRows>;
}

const apiPaperJournalService: IPaperJournalService = {
  async getPaperJournalUploads(
    params: PaperJournalListUploadsParams,
  ): Promise<PaperJournalUploadHistoryResponse> {
    const queryParams: Record<string, string | number> = {
      limit: params.limit,
      offset: params.offset,
    };
    if (params.uploaded_by !== undefined && params.uploaded_by !== null) {
      queryParams.uploaded_by = params.uploaded_by;
    }
    if (params.from_date !== undefined && params.from_date !== null) {
      queryParams.from_date = params.from_date;
    }
    if (params.to_date !== undefined && params.to_date !== null) {
      queryParams.to_date = params.to_date;
    }
    if (params.summary_window !== undefined && params.summary_window !== null) {
      queryParams.summary_window = params.summary_window;
    }

    const response = await api.get<PaperJournalUploadHistoryResponse>(
      API_ENDPOINTS.admin.paperJournalUploads,
      { params: queryParams },
    );
    return response.data;
  },

  async uploadPaperJournal(file: File): Promise<PaperJournalUploadResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await api.post<PaperJournalUploadResponse>(
      API_ENDPOINTS.admin.paperJournalUploads,
      formData,
    );
    return response.data;
  },

  async getPaperJournalUploadRows(fileSha256: string): Promise<PaperJournalRewardRow[]> {
    const response = await api.get<PaperJournalPaginatedRewardRows>(
      API_ENDPOINTS.admin.paperJournalUploadRows(fileSha256),
    );
    return response.data.items;
  },

  async getPaperJournalUserRows(
    refUserId: number,
    limit: number,
    offset: number,
  ): Promise<PaperJournalPaginatedRewardRows> {
    const response = await api.get<PaperJournalPaginatedRewardRows>(
      API_ENDPOINTS.admin.paperJournalRows,
      { params: { ref_user_id: refUserId, limit, offset } },
    );
    return response.data;
  },

  async getPaperJournalEpidRows(
    eligibleParticipantId: string | number,
    limit: number,
    offset: number,
  ): Promise<PaperJournalPaginatedRewardRows> {
    const response = await api.get<PaperJournalPaginatedRewardRows>(
      API_ENDPOINTS.admin.paperJournalRows,
      { params: { eligible_participant_id: eligibleParticipantId, limit, offset } },
    );
    return response.data;
  },
};

export const paperJournalService: IPaperJournalService = apiPaperJournalService;

export default paperJournalService;
